import { useMemo, useRef } from 'react';
import type { components } from '../../../types/schema';
import { useGetSceneEpisodeParagraphs } from '../../../domains/scene/ui/hooks/useGetSceneEpisodeParagraphs';
import { useColumnSnapPages } from '../hooks/useColumnSnapPages';
import EpisodeBlock from './EpisodeBlock';

const PARAGRAPHS_LIMIT = 100;

type Episode = components['schemas']['ApiV1SceneEpisode'];
interface EpisodeCardsProps {
  sceneId: string;
  episode: Episode;
}

const EpisodeCards = ({ sceneId, episode }: EpisodeCardsProps) => {
  const { data: paragraphsData, isLoading } = useGetSceneEpisodeParagraphs(
    sceneId,
    episode.sceneEpisodeId ?? '',
    { limit: PARAGRAPHS_LIMIT, direction: 'after' },
    { enabled: true }
  );

  const paragraphs = useMemo(
    () => paragraphsData?.pages.flatMap((page) => page.sceneEpisodeParagraphs ?? []) ?? [],
    [paragraphsData]
  );

  const contentRef = useRef<HTMLDivElement>(null);
  const totalPages = useColumnSnapPages(contentRef, paragraphs);

  return (
    <div className="contents">
      <div
        data-scene-episode-id={episode.sceneEpisodeId ?? ''}
        className="relative h-full flex-none"
      >
        <div
          ref={contentRef}
          className="h-full inline-block align-top overflow-hidden bg-background-light dark:bg-background-dark"
          style={{
            columnWidth: '100vw',
            columnFill: 'auto',
            columnGap: 0,
            width: 'auto'
          }}
        >
          {isLoading && paragraphs.length === 0 ? (
            <div className="w-screen px-[17px] py-[16px] animate-pulse">
              <div className="h-[20px] w-[80px] rounded bg-black-opacity-200 dark:bg-white-opacity-200 mb-[12px]" />
              <div className="h-[20px] w-full rounded bg-black-opacity-200 dark:bg-white-opacity-200" />
            </div>
          ) : (
            <EpisodeBlock sceneEpisodeId={episode.sceneEpisodeId ?? ''} paragraphs={paragraphs} />
          )}
        </div>

        <div className="absolute top-0 left-0 w-full h-full flex pointer-events-none z-10">
          {Array.from({ length: totalPages }).map((_, i) => (
            <div
              key={`snap-${episode.sceneEpisodeId}-${i}`}
              className="relative w-screen h-full flex-none snap-start snap-always"
            >
              <div
                className="absolute top-0 left-0 bottom-0 w-[24px] z-20"
                style={{
                  background: 'linear-gradient(to right, rgba(0,0,0,0.03), transparent)'
                }}
              />
              <div
                className="absolute top-0 right-0 bottom-0 w-[24px] z-20"
                style={{
                  background: 'linear-gradient(to left, rgba(0,0,0,0.03), transparent)'
                }}
              />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default EpisodeCards;
